import { useCallback, useEffect, useMemo, useRef, useState } from "react"

import { isAutomaticExpectedBehaviorEnabled } from "@/metrics/registry"

import { SimulatorType } from "@/types/api/SimulatorType"
import type { TestSetRecord } from "@/types/api/TestSetRecord"
import { isJsonObject } from "@/types/json"

import {
  getExpectedBehaviorCompositeMetricName,
  hasSelectedTestSetWithExpectedBehaviors,
} from "./evaluationLauncherState"
import type {
  SimulationLaunchMetricRecordsByName,
  SimulationTemplateData,
} from "./types"

interface UseSimpleEvaluationStateOptions {
  initialTemplateData?: SimulationTemplateData
  selectedTestSetId?: string
  selectedTestSetIds?: string[]
  selectedAgentId?: string
  selectedMetricNames?: string[]
  hasExternalTemplate?: boolean
  testSetRecords?: TestSetRecord[]
  metricRecords?: SimulationLaunchMetricRecordsByName
}

const DEFAULT_TEMPLATE_DATA: SimulationTemplateData = {
  templateId: "",
  name: "",
  description: "",
  testSetIds: [],
  enabledMetrics: [],
  frequency: "none",
  agentId: "",
  agentIds: [],
  simulationType: SimulatorType.VOICE,
  simulationMetadata: {},
  concurrency: 5,
  iterationCount: 1,
  shouldSubSample: false,
  subSampleSize: undefined,
  selectedTestCaseIds: undefined,
  personaIds: [],
  firstMessageMode: "assistant-waits-for-user",
  testCaseParamsByIteration: [],
}

function normalizeSimulationMetadata(
  simulationMetadata: SimulationTemplateData["simulationMetadata"] | undefined,
): SimulationTemplateData["simulationMetadata"] {
  return isJsonObject(simulationMetadata) ? simulationMetadata : {}
}

interface BuildEmptyOptions {
  selectedTestSetId?: string
  selectedTestSetIds?: string[]
  selectedAgentId?: string
  selectedMetricNames?: string[]
}

function buildEmptyTemplateData(
  selectedTestSetIdOrOptions?: string | BuildEmptyOptions,
): SimulationTemplateData {
  const options: BuildEmptyOptions =
    typeof selectedTestSetIdOrOptions === "string"
      ? { selectedTestSetId: selectedTestSetIdOrOptions }
      : (selectedTestSetIdOrOptions ?? {})

  const testSetIds = options.selectedTestSetIds?.length
    ? options.selectedTestSetIds
    : options.selectedTestSetId
      ? [options.selectedTestSetId]
      : []

  return {
    ...DEFAULT_TEMPLATE_DATA,
    testSetIds,
    agentId: options.selectedAgentId ?? "",
    agentIds: options.selectedAgentId ? [options.selectedAgentId] : [],
    enabledMetrics: options.selectedMetricNames ?? [],
  }
}

function normalizeTemplateData(
  template: SimulationTemplateData,
  options?: BuildEmptyOptions,
): SimulationTemplateData {
  const selectedTestSetId = options?.selectedTestSetId

  // Template/resimulate values override query param pre-selections
  const testSetIds = selectedTestSetId
    ? [selectedTestSetId]
    : template.testSetIds?.length
      ? template.testSetIds
      : []

  return {
    ...buildEmptyTemplateData(options),
    ...template,
    description: template.description || "",
    testSetIds,
    enabledMetrics: template.enabledMetrics || [],
    agentId: template.agentIds?.[0] || template.agentId || "",
    agentIds: template.agentIds?.length
      ? template.agentIds
      : template.agentId
        ? [template.agentId]
        : [],
    simulationType: template.simulationType || SimulatorType.VOICE,
    simulationMetadata: normalizeSimulationMetadata(
      template.simulationMetadata,
    ),
    concurrency: template.concurrency ?? DEFAULT_TEMPLATE_DATA.concurrency,
    iterationCount:
      template.iterationCount || DEFAULT_TEMPLATE_DATA.iterationCount,
    shouldSubSample: Boolean(template.subSampleSize),
    personaIds: template.personaIds || [],
    testCaseParamsByIteration: template.testCaseParamsByIteration || [],
  }
}

/**
 * useEvaluationState - Evaluation state management
 *
 * Uses SimulationTemplateData format directly to eliminate format conversion overhead
 * and complex synchronization logic. Includes internal stable reference protection
 * to prevent data loss during refetches without requiring external stable ref management.
 */
export function useEvaluationState({
  initialTemplateData,
  selectedTestSetId,
  selectedTestSetIds,
  selectedAgentId,
  selectedMetricNames,
  hasExternalTemplate,
  testSetRecords = [],
  metricRecords = {},
}: UseSimpleEvaluationStateOptions = {}) {
  const buildOptions: BuildEmptyOptions = useMemo(
    () => ({
      selectedTestSetId,
      selectedTestSetIds,
      selectedAgentId,
      selectedMetricNames,
    }),
    [
      selectedTestSetId,
      selectedTestSetIds,
      selectedAgentId,
      selectedMetricNames,
    ],
  )
  // Internal stable reference management - prevents unnecessary resets on refetch
  const stableTemplateRef = useRef<SimulationTemplateData | undefined>(
    initialTemplateData,
  )

  // Track the last template ID we initialized from to prevent unnecessary resets
  const lastInitializedTemplateIdRef = useRef<string | undefined>(undefined)

  // Track if the user has made any changes
  const hasUserChangesRef = useRef(false)
  const expectedBehaviorSelectionsRef = useRef(new Set<string>())

  // Track previous hasExternalTemplate value for detecting navigation away from resimulate
  const prevHasExternalTemplateRef = useRef<boolean | undefined>(undefined)

  // Update stable reference only when template ID actually changes (not just refetched data)
  useEffect(() => {
    if (
      initialTemplateData?.templateId !== stableTemplateRef.current?.templateId
    ) {
      stableTemplateRef.current = initialTemplateData
    }
  }, [initialTemplateData])

  // Create initial template data from template record or defaults
  const createInitialTemplateData = useCallback(
    (template?: SimulationTemplateData): SimulationTemplateData =>
      template
        ? normalizeTemplateData(template, buildOptions)
        : buildEmptyTemplateData(buildOptions),
    [buildOptions],
  )

  const [templateData, setTemplateData] = useState<SimulationTemplateData>(
    () => {
      // Initialize with stable template if provided
      if (stableTemplateRef.current) {
        lastInitializedTemplateIdRef.current =
          stableTemplateRef.current.templateId
      }
      return createInitialTemplateData(stableTemplateRef.current)
    },
  )

  const [metricSetupRevision, setMetricSetupRevision] = useState(0)
  const firstAgentId = templateData.agentIds?.[0] ?? ""
  const simulatorType = templateData.simulationType
  const recordSetupChange = useCallback(
    (updates: Partial<SimulationTemplateData>) => {
      const nextAgentId =
        updates.agentIds === undefined
          ? firstAgentId
          : (updates.agentIds[0] ?? "")
      if (
        nextAgentId !== firstAgentId ||
        (updates.simulationType ?? simulatorType) !== simulatorType
      ) {
        setMetricSetupRevision((revision) => revision + 1)
      }
    },
    [firstAgentId, simulatorType],
  )

  const hasExpectedBehaviorTestSet = useMemo(
    () =>
      hasSelectedTestSetWithExpectedBehaviors(
        testSetRecords,
        templateData.testSetIds,
      ),
    [templateData.testSetIds, testSetRecords],
  )
  const expectedBehaviorMetricName = useMemo(
    () => getExpectedBehaviorCompositeMetricName(metricRecords),
    [metricRecords],
  )
  const isTemplatePrefill =
    hasExternalTemplate || Boolean(stableTemplateRef.current?.templateId)

  useEffect(() => {
    if (
      isAutomaticExpectedBehaviorEnabled() ||
      isTemplatePrefill ||
      !hasExpectedBehaviorTestSet ||
      !expectedBehaviorMetricName
    ) {
      return
    }

    const selectionKey = JSON.stringify([...templateData.testSetIds].sort())
    if (expectedBehaviorSelectionsRef.current.has(selectionKey)) {
      return
    }

    expectedBehaviorSelectionsRef.current.add(selectionKey)
    setTemplateData((previous) =>
      previous.enabledMetrics.includes(expectedBehaviorMetricName)
        ? previous
        : {
            ...previous,
            enabledMetrics: [
              ...previous.enabledMetrics,
              expectedBehaviorMetricName,
            ],
          },
    )
  }, [
    expectedBehaviorMetricName,
    hasExpectedBehaviorTestSet,
    isTemplatePrefill,
    templateData.testSetIds,
  ])

  // Handle template changes with stable reference protection
  useEffect(() => {
    const currentTemplate = stableTemplateRef.current

    if (
      currentTemplate?.templateId &&
      currentTemplate.templateId !== lastInitializedTemplateIdRef.current &&
      !hasUserChangesRef.current // Only auto-reset if user hasn't made changes
    ) {
      lastInitializedTemplateIdRef.current = currentTemplate.templateId

      setTemplateData((prev) => ({
        ...createInitialTemplateData(currentTemplate),
        // Template values override query param pre-selections
        testSetIds: selectedTestSetId
          ? [selectedTestSetId]
          : currentTemplate.testSetIds?.length
            ? currentTemplate.testSetIds
            : prev.testSetIds,
      }))
    }
  }, [
    initialTemplateData?.templateId,
    selectedTestSetId,
    createInitialTemplateData,
  ])

  // Apply query param pre-selections when they arrive asynchronously
  // (e.g. metric names depend on loaded metric records)
  const hasAppliedQueryParamsRef = useRef(false)
  useEffect(() => {
    if (
      hasAppliedQueryParamsRef.current ||
      hasUserChangesRef.current ||
      hasExternalTemplate
    ) {
      return
    }

    const hasPreSelections =
      (selectedAgentId && !templateData.agentIds?.length) ||
      (selectedTestSetIds?.length && !templateData.testSetIds?.length) ||
      (selectedMetricNames?.length && !templateData.enabledMetrics?.length)

    if (hasPreSelections) {
      hasAppliedQueryParamsRef.current = true
      setTemplateData((prev) => ({
        ...prev,
        ...(selectedAgentId && !prev.agentIds?.length
          ? { agentIds: [selectedAgentId], agentId: selectedAgentId }
          : {}),
        ...(selectedTestSetIds?.length && !prev.testSetIds?.length
          ? { testSetIds: selectedTestSetIds }
          : {}),
        ...(selectedMetricNames?.length && !prev.enabledMetrics?.length
          ? { enabledMetrics: selectedMetricNames }
          : {}),
      }))
    }
  }, [
    selectedAgentId,
    selectedTestSetIds,
    selectedMetricNames,
    hasExternalTemplate,
    templateData.agentIds?.length,
    templateData.testSetIds?.length,
    templateData.enabledMetrics?.length,
  ])

  // Detect navigation away from resimulate (transition from true -> false)
  // This clears the state when user clicks "New Simulation" after "Resimulate"
  useEffect(() => {
    const prevValue = prevHasExternalTemplateRef.current

    if (prevValue === true && hasExternalTemplate === false) {
      stableTemplateRef.current = undefined
      lastInitializedTemplateIdRef.current = undefined
      hasUserChangesRef.current = false
      expectedBehaviorSelectionsRef.current.clear()
      setTemplateData(createInitialTemplateData())
    }

    prevHasExternalTemplateRef.current = hasExternalTemplate
  }, [hasExternalTemplate, createInitialTemplateData])

  // Update template data - can handle partial updates or complete replacement
  const updateTemplateData = useCallback(
    (updates: Partial<SimulationTemplateData> | SimulationTemplateData) => {
      recordSetupChange(updates)
      hasUserChangesRef.current = true // Mark that user has made changes
      setTemplateData((prev) => ({ ...prev, ...updates }))
    },
    [recordSetupChange],
  )

  // Load from template record
  const loadTemplate = useCallback(
    (template: SimulationTemplateData) => {
      // Update both stable reference and tracking
      stableTemplateRef.current = template
      lastInitializedTemplateIdRef.current = template.templateId
      hasUserChangesRef.current = false // Reset user changes flag when explicitly loading a template
      expectedBehaviorSelectionsRef.current.clear()

      const next = createInitialTemplateData(template)
      setMetricSetupRevision((revision) => revision + 1)
      setTemplateData(next)
    },
    [createInitialTemplateData],
  )

  // Clear template and reset to defaults
  const clearTemplate = useCallback(() => {
    // Clear all references
    stableTemplateRef.current = undefined
    lastInitializedTemplateIdRef.current = undefined
    hasUserChangesRef.current = false // Reset user changes flag
    expectedBehaviorSelectionsRef.current.clear()

    const next = buildEmptyTemplateData(buildOptions)
    setMetricSetupRevision((revision) => revision + 1)
    setTemplateData(next)
  }, [buildOptions])

  return {
    templateData,
    metricSetupRevision,
    updateTemplateData,
    loadTemplate,
    clearTemplate,
    // Expose the stable template reference for external components that need it
    stableTemplateData: stableTemplateRef.current,
  }
}
